import React from "react"

import type { LineageLogEntry, LineageScript } from "types/lineage.types"
import { commandLine, groupLogByCall, isDataLoad, sessionCalls } from "../utils/lineageCalls"
import { scriptLabel, ScriptLabelOptions } from "../utils/traceTable"

interface ScriptLogProps {
  /** The script log (schema 3) */
  scripts?: LineageScript[]
  /** The command log (schema 4); shown instead of `scripts` when given */
  log?: LineageLogEntry[]
  engineVersion?: string
  /** What the view that opened the log knows about its own scripts, to name them */
  labels?: ScriptLabelOptions
}

const OutcomeTag: React.FC<{ outcome: "ok" | "error" }> = ({ outcome }) => (
  <span
    className={`ml-auto rounded px-1.5 py-px text-[11px] font-medium ${
      outcome === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
    }`}
  >
    {outcome === "ok" ? "Ran to the end" : "Stopped on an error"}
  </span>
)

const CardHeader: React.FC<{ n: number; title: string; outcome: "ok" | "error" }> = ({ n, title, outcome }) => (
  <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[12px]">
    <span className="font-medium text-slate-600">{n}</span>
    <span className="text-slate-600">{title}</span>
    <OutcomeTag outcome={outcome} />
  </div>
)

// A data load as a line: its SHA-256, and the root label it loaded under when the engine gave one
const dataLoadText = (e: LineageLogEntry, prefix: boolean): string =>
  `${prefix ? "Data loaded, " : ""}SHA-256 ${e.dataHash ?? ""}${e.rootLabel ? `, root label ${e.rootLabel}` : ""}`

// Schema 4: each call with its commands, one per line, the error under the command that failed
const CallLog: React.FC<{ log: LineageLogEntry[]; labels?: ScriptLabelOptions }> = ({ log, labels }) => {
  const calls = sessionCalls({ log })
  return (
    <ol className="space-y-3">
      {groupLogByCall(log).map((c, i) => (
        <li key={i} className="rounded border border-slate-200">
          <CardHeader n={i + 1} title={scriptLabel(calls, i, labels)} outcome={c.outcome} />
          <div className="py-1.5">
            {c.entries.map(({ index, entry }) => (
              <div key={index}>
                {isDataLoad(entry) ? (
                  <div className="break-all px-3 py-0.5 font-mono text-[12px] text-slate-600">
                    {dataLoadText(entry, !c.dataLoad)}
                  </div>
                ) : (
                  <pre className="m-0 overflow-x-auto whitespace-pre px-3 py-0.5 font-mono text-[12px] text-slate-700">
                    {commandLine(entry)}
                  </pre>
                )}
                {entry.error && <div className="px-3 pb-1 text-[12px] text-red-700">{entry.error}</div>}
              </div>
            ))}
          </div>
        </li>
      ))}
    </ol>
  )
}

// Schema 3: each script as it was sent
const WholeScriptLog: React.FC<{ scripts: LineageScript[]; labels?: ScriptLabelOptions }> = ({ scripts, labels }) => {
  const calls = sessionCalls({ scripts })
  return (
    <ol className="space-y-3">
      {scripts.map((s, i) => (
        <li key={i} className="rounded border border-slate-200">
          <CardHeader n={i + 1} title={scriptLabel(calls, i, labels)} outcome={s.outcome} />
          {s.kind === "create" ? (
            <div className="break-all px-3 py-2 font-mono text-[12px] text-slate-600">SHA-256 {s.dataHash}</div>
          ) : (
            <pre className="m-0 overflow-x-auto whitespace-pre px-3 py-2 font-mono text-[12px] text-slate-700">
              {s.script}
            </pre>
          )}
          {s.error && <div className="border-t border-red-100 px-3 py-1.5 text-[12px] text-red-700">{s.error}</div>}
        </li>
      ))}
    </ol>
  )
}

/**
 * The session's log: every call that changed the session, in the order it ran (schema 4 lists each
 * call's commands as the engine ran them, schema 3 each script as it was sent). Running these again
 * on the same data with the same engine gives the same session, so this is the full record behind
 * the lineage graph.
 */
export const ScriptLog: React.FC<ScriptLogProps> = ({ scripts, log, engineVersion, labels }) => (
  <div className="h-full overflow-auto px-4 py-3">
    {engineVersion && <div className="mb-3 text-[12px] text-slate-500">Query engine {engineVersion}</div>}
    {log ? <CallLog log={log} labels={labels} /> : <WholeScriptLog scripts={scripts ?? []} labels={labels} />}
  </div>
)

export default ScriptLog
